import MultiObject from "./data-output-generator/output/assets/objects/multi/multi-object.js";
import GLRaycastRenderer from "./glraycast-renderer.js";
import GLRaycastDebugCanvas from "./glraycast-debug-canvas.js";

import DataOutputGenerator from "./data-output-generator.js";

const GLRAYCAST_DEBUG_CANVAS = false;

class Viewer {
    constructor(
        { struct: { wrappers: wrapCtx, events: evtCtx }, shallow: { wrappers: shlWrapCtx, events: shlEvtCtx } },
        { fileArrayBufLoader, gltfLoader, texLoader, obj2dUrlPrefix },
    ) {
        this._wrapCtx = wrapCtx;
        this._evtCtx = evtCtx;
        this._shlWrapCtx = shlWrapCtx;
        this._shlEvtCtx = shlEvtCtx;

        this._domElement = document.createElement("div");
        Object.assign(this._domElement.style, {
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
        });

        this._size = { width: 300, height: 150 };

        this._canvas = document.createElement("canvas");
        this._context = this._canvas.getContext("webgl");

        this._canvas.style.pointerEvents = "none";

        this._layerRoot = document.createElement("div");
        Object.assign(this._layerRoot.style, {
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
        });

        this._labelRoot = document.createElement("div");
        Object.assign(this._labelRoot.style, {
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
        });

        this._domElement.appendChild(this._canvas);
        this._domElement.appendChild(this._layerRoot);
        this._domElement.appendChild(this._labelRoot);

        this._scene = new MultiObject({
            visual: new THREE.Scene(),
            glraycast: new THREE.Scene(),
        });
        this._camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);

        this._glraycastRenderer = null;
        if (GLRAYCAST_DEBUG_CANVAS) {
            this._glraycastDebugCanvas = new GLRaycastDebugCanvas();
            this._domElement.appendChild(this._glraycastDebugCanvas.canvas);
        }

        //const light = new THREE.PointLight();
        //light.position.set(2.5, 1, 2.5);
        //this._scene.add(light);

        this._threeLoaders = {
            fileArrayBufLoader,
            gltfLoader,
            texLoader,
        };

        this._obj2dUrlPrefix = obj2dUrlPrefix;

        this._assetDataToThree = null;
        this._renderer = null;

        this._running = false;
    }

    get domElement() {
        return this._domElement;
    }

    start(tour, tourLabels, session) {
        if (this._running) {
            throw new Error("Viewer is already running");
        }

        this._running = true;

        this._renderer = new THREE.WebGLRenderer({ canvas: this._canvas, context: this._context });

        this._glraycastRenderer = new GLRaycastRenderer(this._scene.objects.glraycast, this._camera, this._renderer);
        if (GLRAYCAST_DEBUG_CANVAS) {
            this._glraycastDebugCanvas.start(this._glraycastRenderer.buffer);
        }

        {
            const { width, height } = this._size;

            this._renderer.setSize(width, height);
            this._renderer.setViewport(0, 0, width, height);

            const [rcWidth, rcHeight] = calcRaycastSize(width, height);
            this._glraycastRenderer.setSize(rcWidth, rcHeight);
        }

        this._glraycastRenderer.start();

        const params = {
            rootObject: this._scene,
            camera: this._camera,
            threeFileArrayBufLoader: this._threeLoaders.fileArrayBufLoader,
            threeGltfLoader: this._threeLoaders.gltfLoader,
            threeTexLoader: this._threeLoaders.texLoader,
            obj2dDomElement: this._layerRoot,
            obj2dUrlPrefix: this._obj2dUrlPrefix,
            labelsDomElement: this._labelRoot,
            glraycastBuffer: this._glraycastRenderer.buffer,
        };

        this._dataOutputGenerator = new DataOutputGenerator(
            { struct: { wrappers: this._wrapCtx, events: this._evtCtx }, shallow: { wrappers: this._shlWrapCtx, events: this._shlEvtCtx } },
            { tour, tourLabels, session },
            params
        );
        this._dataOutputGenerator.start();
    }

    stop() {
        if (!this._running) {
            throw new Error("Viewer is not running");
        }

        this._running = false;

        if (GLRAYCAST_DEBUG_CANVAS) {
            this._glraycastDebugCanvas.stop();
        }
        this._glraycastRenderer.stop();
        this._glraycastRenderer = null;

        this._dataOutputGenerator.stop();
        this._dataOutputGenerator = null;

        this._renderer.dispose();
        this._renderer = null;
    }

    setSize(width, height) {
        if (width === this._size.width && height === this._size.height) {
            return;
        }

        this._size.width = width;
        this._size.height = height;

        this._camera.aspect = width/height;
        this._camera.updateProjectionMatrix();

        if (this._running) {
            this._renderer.setSize(width, height);
            this._renderer.setViewport(0, 0, width, height);

            const [rcWidth, rcHeight] = calcRaycastSize(width, height);
            this._glraycastRenderer.setSize(rcWidth, rcHeight);
        }
    }

    render() {
        this._dataOutputGenerator.update();
        this._renderer.render(this._scene.objects.visual, this._camera);
    }
}

function calcRaycastSize(width, height) {
    return [Math.max(1, Math.round(width*0.25)), Math.max(1, Math.round(height*0.25))];
}

export default Viewer;
